export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type ApiErrorType =
  | 'network'
  | 'unauthorized'
  | 'rate_limited'
  | 'validation'
  | 'not_found'
  | 'server'
  | 'unknown';

/**
 * One field the backend refused, as its schema names it. `path` is the zod
 * issue path joined with `/` — `scenario/extra_instructions`.
 */
export interface ApiErrorIssue {
  path: string;
  code: string;
  maximum?: number;
  minimum?: number;
}

export class ApiError extends Error {
  public readonly type: ApiErrorType;
  public readonly status: number | null;
  /** The backend's machine-readable reason, when it sends one. */
  public readonly code: string | null;
  /**
   * The backend's own sentence about this failure, when it sends one — "Topic
   * set is in use by 3 tracks".
   *
   * Separate from `message`, which stays the generic line naming the request
   * that failed. That line is what Sentry groups on, and a backend sentence
   * carrying a count or a name would scatter one fault across a hundred
   * issues. A screen that wants to tell the user *why* reaches for this.
   */
  public readonly detail: string | null;
  /**
   * Which fields the backend refused, when it refused fields. A screen that
   * draws a form reaches for this to mark them; empty for every other failure.
   */
  public readonly issues: readonly ApiErrorIssue[];
  /**
   * The failure's JSON body as the backend sent it, for the few refusals that
   * carry a field of their own (the running journey a create collided with).
   * Read it through that domain's helper, which narrows it; null without one.
   */
  public readonly body: unknown;

  constructor(
    message: string,
    type: ApiErrorType,
    status: number | null = null,
    code: string | null = null,
    detail: string | null = null,
    issues: readonly ApiErrorIssue[] = [],
    body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.issues = issues;
    this.body = body;
  }
}

interface ErrorBody {
  code: string | null;
  detail: string | null;
  issues: ApiErrorIssue[];
  body: unknown;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function issueOrNull(entry: unknown): ApiErrorIssue | null {
  if (typeof entry !== 'object' || entry === null) return null;

  const { path, code, maximum, minimum } = entry as Record<string, unknown>;
  if (typeof path !== 'string' || typeof code !== 'string') return null;

  return {
    path,
    code,
    ...(typeof maximum === 'number' ? { maximum } : {}),
    ...(typeof minimum === 'number' ? { minimum } : {}),
  };
}

function issuesFrom(value: unknown): ApiErrorIssue[] {
  if (!Array.isArray(value)) return [];

  return (value as unknown[])
    .map(issueOrNull)
    .filter((issue): issue is ApiErrorIssue => issue !== null);
}

/* Failures are still failures if the body is unreadable — never throw from here. */
async function errorBodyFrom(response: Response): Promise<ErrorBody> {
  try {
    const body: unknown = await response.clone().json();
    if (typeof body === 'object' && body !== null) {
      const { code, message, issues } = body as {
        code?: unknown;
        message?: unknown;
        issues?: unknown;
      };
      return {
        code: textOrNull(code),
        detail: textOrNull(message),
        issues: issuesFrom(issues),
        body,
      };
    }
  } catch {
    /* no body, or not JSON */
  }
  return { code: null, detail: null, issues: [], body: null };
}

/**
 * Whether a failed request might succeed if simply asked again.
 *
 * A refusal — any 4xx the backend answered with — is a verdict, and asking the
 * same question again gets the same answer while the user watches a spinner.
 * Network failures and server errors are the transient cases retrying exists
 * for.
 */
export function isTransientFailure(error: unknown): boolean {
  if (error instanceof ApiError && error.status !== null) {
    return error.status >= 500;
  }
  return true;
}

function errorTypeFor(status: number): ApiErrorType {
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 404) return 'not_found';
  if (status === 422) return 'validation';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'unknown';
}

/**
 * The one way this app talks to the backend. Cookies are always sent — the
 * session lives in a cookie on the API origin, never in local storage.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        /*
         * Only a body has a type to name, and only the JSON one is ours to
         * name. A request without a body that claims to send JSON is not
         * merely untrue: it is what makes the browser ask permission first,
         * so every GET pays for an OPTIONS round trip before it is sent. A
         * multipart body's content type carries the boundary that separates
         * its parts, and only the browser knows what it generated — naming a
         * type there would hand the backend a body it cannot parse, so an
         * upload is left to state its own.
         */
        ...(typeof init.body === 'string' ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError('Could not reach the server', 'network');
  }

  if (!response.ok) {
    const { code, detail, issues, body } = await errorBodyFrom(response);

    throw new ApiError(
      `Request to ${path} failed`,
      errorTypeFor(response.status),
      response.status,
      code,
      detail,
      issues,
      body,
    );
  }

  return response;
}

export async function apiFetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await apiFetch(path, init);
  return response.json() as Promise<T>;
}

export async function fetchHealth(): Promise<boolean> {
  try {
    await apiFetch('/health');
    return true;
  } catch {
    return false;
  }
}
